/* KD9 — AuthContext.
   The backend access map is the single frontend authority for roles,
   permissions and flags. RoleContext only receives the derived visual role. */
import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { useRole } from "@/context/RoleContext";

/* Backend role → existing frontend role label used by RoleContext/RoleMeta. */
const BACKEND_TO_FRONTEND_ROLE = {
  director: "superadmin",
  admin: "admin",
  support: "soporte",
  consultant: "consultor",
  creator: "creador",
  student: "estudiante",
  user: "user",
};

const ROLE_PRIORITY = ["director", "admin", "support", "consultant", "creator", "student", "user"];

const AuthCtx = createContext({
  user: null,
  accessMap: null,
  blocked: false,
  blockedMessage: "",
  ready: false,
  login: async () => {},
  register: async () => {},
  logout: async () => {},
  refresh: async () => {},
});

export const AuthProvider = ({ children }) => {
  const { setRole } = useRole();
  const [user, setUser] = useState(null);
  const [accessMap, setAccessMap] = useState(null);
  const [blocked, setBlocked] = useState(false);
  const [blockedMessage, setBlockedMessage] = useState("");
  const [ready, setReady] = useState(false);

  const applyAccessMap = useCallback((amap) => {
    setAccessMap(amap);
    if (!amap?.roles?.length) return;
    const primary = ROLE_PRIORITY.find((r) => amap.roles.includes(r)) || "user";
    const feRole = BACKEND_TO_FRONTEND_ROLE[primary] || "user";
    setRole(feRole);
  }, [setRole]);

  const fetchAccessMap = useCallback(async () => {
    const { data } = await api.get("/api/me/access-map");
    applyAccessMap(data);
    return data;
  }, [applyAccessMap]);

  const refreshSession = useCallback(async () => {
    const [{ data: currentUser }, { data: currentAccessMap }] = await Promise.all([
      api.get("/api/auth/me"),
      api.get("/api/me/access-map"),
    ]);
    setUser(currentUser);
    applyAccessMap(currentAccessMap);
    return { user: currentUser, accessMap: currentAccessMap };
  }, [applyAccessMap]);

  useEffect(() => {
    const resetSessionState = () => {
      setUser(null);
      setAccessMap(null);
      setRole("user");
      setReady(true);
    };
    const handleBlocked = (event) => {
      resetSessionState();
      setBlocked(true);
      setBlockedMessage(event?.detail?.message || "Cuenta bloqueada. Comunícate con Soporte KD9.");
    };
    const handleExpired = () => {
      resetSessionState();
      setBlocked(false);
      setBlockedMessage("");
    };
    window.addEventListener("kd9-account-blocked", handleBlocked);
    window.addEventListener("kd9-session-expired", handleExpired);
    return () => {
      window.removeEventListener("kd9-account-blocked", handleBlocked);
      window.removeEventListener("kd9-session-expired", handleExpired);
    };
  }, [setRole]);

  // Silent session check on mount. User identity and access-map are refreshed
  // together so role/flag changes cannot leave a stale topbar or menu.
  useEffect(() => {
    (async () => {
      try {
        await refreshSession();
      } catch (_) {
        setUser(null);
        setAccessMap(null);
      } finally {
        setReady(true);
      }
    })();
  }, [refreshSession]);

  // If Director/Admin changes this account from another session/device, sync
  // the effective role as soon as the user returns to the app.
  useEffect(() => {
    if (!user) return undefined;
    let syncing = false;
    const syncForegroundSession = async () => {
      if (document.hidden || syncing) return;
      syncing = true;
      try {
        await refreshSession();
      } catch (_) {
        // 401/403 handling is centralized in the API interceptor.
      } finally {
        syncing = false;
      }
    };
    window.addEventListener("focus", syncForegroundSession);
    document.addEventListener("visibilitychange", syncForegroundSession);
    return () => {
      window.removeEventListener("focus", syncForegroundSession);
      document.removeEventListener("visibilitychange", syncForegroundSession);
    };
  }, [user, refreshSession]);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await api.post("/api/auth/login", { email, password });
      setBlocked(false);
      setBlockedMessage("");
      setUser(data);
      await fetchAccessMap();
      return { ok: true, user: data };
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 428 && detail?.code === "WHATSAPP_VERIFICATION_REQUIRED") {
        return {
          ok: false,
          verificationRequired: true,
          activationToken: detail.activation_token,
          phoneMasked: detail.phone_masked,
          error: detail.message,
        };
      }
      return { ok: false, error: formatApiError(e) };
    }
  }, [fetchAccessMap]);

  const register = useCallback(async (payload) => {
    try {
      const { data } = await api.post("/api/auth/register", payload);
      if (data?.verification_required) {
        return {
          ok: true,
          verificationRequired: true,
          activationToken: data.activation_token,
          phoneMasked: data.phone_masked,
          otpSent: data.otp_sent,
          deliveryStatus: data.delivery_status,
          expiresInSeconds: data.expires_in_seconds,
          message: data.message,
        };
      }
      setUser(data);
      await fetchAccessMap();
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  }, [fetchAccessMap]);

  const logout = useCallback(async () => {
    try {
      await api.post("/api/auth/logout");
    } catch (_) {
      /* ignore — local state still cleared below */
    }
    setUser(null);
    setAccessMap(null);
    setBlocked(false);
    setBlockedMessage("");
    setRole("user");
    localStorage.removeItem("kd9-server-permissions");
    localStorage.removeItem("kd9-server-flags");
    // Legacy keys from earlier mock RoleSwitcher — purge to avoid stale state.
    localStorage.removeItem("kd9-role");
    localStorage.removeItem("kd9-flags");
    localStorage.removeItem("kd9-requests");
  }, [setRole]);

  return (
    <AuthCtx.Provider value={{ user, accessMap, blocked, blockedMessage, ready, login, register, logout, refresh: refreshSession }}>
      {children}
    </AuthCtx.Provider>
  );
};

export const useAuth = () => useContext(AuthCtx);
